import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { Button } from '@framework/ui/atoms/Button';
import type { UserEntity } from '@user/UserEntity';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import type { ViewModelId } from 'balus-common';
import { trpcClient } from '@framework/trpcClient';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  memberData: Pick<UserEntity, 'id' | 'name'>;
  viewModelId: ViewModelId;
  viewModelName: string;
  onDeleted(): void;
  onClose(): void;
};

export const ViewModelIndividualPermissionDeleteConfirmModal: React.FC<Props> = ({
  isOpen,
  memberData,
  viewModelId,
  viewModelName,
  onDeleted,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const handleSubmit = useCallback(async () => {
    try {
      await trpcClient.viewModel.individualMember.removeMembers.mutate({ viewModelId, userIds: [memberData.id] });
      toast.success(t('group.viewModelPermissionDelete.toast.success'));
      onDeleted();
      onClose();
    } catch (e) {
      toast.error(t('group.viewModelPermissionDelete.toast.error'));
      console.error('Failed to delete individual permission for view model:', {
        viewModelId,
        userId: memberData.id,
        error: e,
      });
    }
  }, [memberData.id, onClose, onDeleted, viewModelId, t]);

  return (
    <GeneralModal
      isOpen={isOpen}
      submitButton={
        <Button color="danger" onClick={handleSubmit}>
          {t('common.action.delete')}
        </Button>
      }
      cancelButton={
        <Button color="secondary" onClick={onClose}>
          {t('common.action.cancel')}
        </Button>
      }
      onClose={onClose}
      shouldCloseOnOverlayClick={false}
      title={t('group.viewModelPermissionDelete.modal.title')}
    >
      <div className="w-[600px] p-4">
        <p className="pb-4">
          {t('group.viewModelPermissionDelete.modal.confirm', { name: memberData.name, viewModelName })}
        </p>
      </div>
    </GeneralModal>
  );
};
